import React from 'react';

import { getFormattedPrice } from '@tools/utils/common.tsx';
import type { ManualDiscountDraft } from '../../types';
import type { PosPromotionPreviewResponse } from '@eshop/types/offers.types';
import { describeManualDiscount } from '../../utils/discountPresentation';

export interface PosFinancialSummaryProps {
  subtotal: number;
  discounts: number;
  orderDiscount?: ManualDiscountDraft | null;
  canEditOrderDiscount?: boolean;
  onEditOrderDiscount?: () => void;
  hasLines?: boolean;
  canManageDiscounts?: boolean;
  discountShortcutLabel?: string | null;
  onOpenManualDiscount?: () => void;
  promotionPreview?: PosPromotionPreviewResponse;
  couponCode?: string;
  promotionsLoading?: boolean;
  promotionsError?: boolean;
  onManageCoupon?: () => void;
  onRetryPromotions?: () => void;
  shippingEnabled: boolean;
  shippingAmount: number;
  shippingAvailable?: boolean;
  shippingLabel?: string;
  onConfigureShipping?: () => void;
  appliedBalanceAmount: number;
  total: number;
  currency: string;
}

export const PosFinancialSummary: React.FC<PosFinancialSummaryProps> = ({
  subtotal,
  discounts,
  orderDiscount,
  canEditOrderDiscount = false,
  onEditOrderDiscount,
  hasLines = false,
  canManageDiscounts = false,
  discountShortcutLabel = 'F6',
  onOpenManualDiscount,
  promotionPreview,
  couponCode,
  promotionsLoading = false,
  promotionsError = false,
  onManageCoupon,
  onRetryPromotions,
  shippingEnabled,
  shippingAmount,
  shippingAvailable = false,
  shippingLabel,
  onConfigureShipping,
  appliedBalanceAmount,
  total,
  currency,
}) => (
  <section className="pos-checkout-summary" aria-label="Resumen de la venta">
    <div className="pos-checkout-summary__row">
      <span>Subtotal</span>
      <span>{getFormattedPrice(subtotal, currency)}</span>
    </div>
    {discounts > 0.009 ? (
      <div className="pos-checkout-summary__row pos-checkout-summary__row--discount">
        <span className="pos-checkout-summary__discount-label">Descuentos</span>
        <span className="pos-checkout-summary__discount-value">
          {orderDiscount ? (
            canEditOrderDiscount && onEditOrderDiscount ? (
              <button
                type="button"
                className="pos-checkout-summary__discount-edit"
                onClick={onEditOrderDiscount}
              >
                {describeManualDiscount(orderDiscount, currency)}
              </button>
            ) : (
              <span>{describeManualDiscount(orderDiscount, currency)}</span>
            )
          ) : null}
          <span>-{getFormattedPrice(discounts, currency)}</span>
        </span>
      </div>
    ) : null}
    {promotionPreview && (promotionPreview.source === 'promotion' || promotionPreview.source === 'mixed')
      ? promotionPreview.applied.map((promotion) => (
          <div
            className="pos-checkout-summary__row pos-checkout-summary__row--promotion"
            key={promotion.promotion_id}
          >
            <span>
              {promotion.coupon_code
                ? `Cupón ${promotion.coupon_code}`
                : promotion.name}
            </span>
            <span>
              -{getFormattedPrice(Number(promotion.discount_total), currency)}
            </span>
          </div>
        ))
      : null}
    {couponCode && promotionPreview?.coupon_status === 'valid_not_better' ? (
      <div className="pos-checkout-summary__notice">
        Cupón {couponCode} válido; la oferta actual ofrece un descuento mayor.
      </div>
    ) : null}
    {couponCode && promotionPreview?.coupon_status === 'rejected' ? (
      <div className="pos-checkout-summary__notice pos-checkout-summary__notice--error">
        No se pudo aplicar el cupón {couponCode}.
      </div>
    ) : null}
    {promotionsError ? (
      <div className="pos-checkout-summary__notice pos-checkout-summary__notice--error">
        No se pudieron revisar las ofertas.
        {onRetryPromotions ? (
          <button type="button" onClick={onRetryPromotions}>
            Reintentar
          </button>
        ) : null}
      </div>
    ) : null}
    <div style={{ display: 'flex', gap: '16px' }}>
      {onOpenManualDiscount ? (
        <button
          type="button"
          className="pos-checkout-summary__discount-action"
          onClick={onOpenManualDiscount}
          disabled={!hasLines || !canManageDiscounts}
        >
          Descuento manual ({discountShortcutLabel})
        </button>
      ) : null}
      {onManageCoupon ? (
        <button
          type="button"
          className="pos-checkout-summary__discount-action"
          onClick={onManageCoupon}
        >
          {couponCode ? `Cambiar cupón ${couponCode}` : 'Agregar cupón'}
          {promotionsLoading ? ' · revisando' : ''}
        </button>
      ) : null}
    </div>
    {shippingAvailable ? (
      <div className="pos-checkout-summary__row">
        <span>
          {shippingEnabled ? (shippingLabel ?? 'Envío') : 'Sin envío'}
        </span>
        <span className="pos-checkout-summary__shipping-value">
          {shippingEnabled
            ? shippingAmount > 0
              ? getFormattedPrice(shippingAmount, currency)
              : 'A convenir'
            : null}
          {onConfigureShipping ? (
            <button type="button" onClick={onConfigureShipping}>
              {shippingEnabled ? 'Editar' : 'Configurar'}
            </button>
          ) : null}
        </span>
      </div>
    ) : null}
    {Math.abs(appliedBalanceAmount) > 0.009 ? (
      <div className="pos-checkout-summary__row">
        <span>
          {appliedBalanceAmount > 0 ? 'Saldo utilizado' : 'Deuda registrada'}
        </span>
        <span>
          {appliedBalanceAmount > 0 ? '-' : '+'}
          {getFormattedPrice(Math.abs(appliedBalanceAmount), currency)}
        </span>
      </div>
    ) : null}
    <div className="pos-checkout-summary__total">
      <span>Total</span>
      <strong>{getFormattedPrice(total, currency)}</strong>
    </div>
  </section>
);

export default PosFinancialSummary;
